import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import apiClient from '../../api/axiosConfig';
import SkeletonLoader from '../../components/SkeletonLoader';
import { API_ENDPOINTS } from '../../constants';
import { Pie, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const BlogAnalytics = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blogTitle, setBlogTitle] = useState('');

  const fetchBlogAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(API_ENDPOINTS.BLOG_ANALYTICS(id));
      if (response.data.success) {
        setAnalyticsData(response.data.data);
        setBlogTitle(response.data.data.blogTitle || response.data.data.summary.blogTitle); // Assuming API returns blogTitle in summary or directly
      } else {
        addToast(response.data.message || 'Failed to fetch blog analytics.', 'error');
        setError(response.data.message || 'Failed to fetch blog analytics.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Error fetching blog analytics.';
      addToast(errorMessage, 'error');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    fetchBlogAnalytics();
  }, [fetchBlogAnalytics]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
        <SkeletonLoader type="form" /> {/* Using form type for a general loading state */}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
        <p className="text-red-500 text-center">{error}</p>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
        <p className="text-text-gray text-center">No analytics data available for this blog.</p>
      </div>
    );
  }

  const openClickRateChartData = {
    labels: ['Clicked', 'Opened (Not Clicked)', 'Not Opened'],
    datasets: [
      {
        data: [
          analyticsData.summary.totalClicked,
          analyticsData.summary.totalOpened - analyticsData.summary.totalClicked,
          analyticsData.summary.totalSent - analyticsData.summary.totalOpened,
        ],
        backgroundColor: ['#60A5FA', '#34D399', '#EF4444'], // Blue for clicked, Green for opened, Red for not opened
        borderColor: ['#3B82F6', '#10B981', '#DC2626'],
        borderWidth: 1,
      },
    ],
  };

  const openClickRateChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#9CA3AF',
        },
      },
      title: {
        display: true,
        text: 'Email Engagement Rate',
        color: '#FFFFFF',
      },
    },
  };

  const hourlyOpensChartData = {
    labels: Object.keys(analyticsData.hourlyOpensData).map(hour => `${hour}:00`),
    datasets: [
      {
        label: 'Emails Opened',
        data: Object.values(analyticsData.hourlyOpensData),
        backgroundColor: '#34D399', // Green color
        borderColor: '#10B981',
        borderWidth: 1,
      },
    ],
  };

  const hourlyOpensChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Emails Opened by Hour of Day',
        color: '#FFFFFF',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Hour of Day (24h)',
          color: '#9CA3AF',
        },
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of Opens',
          color: '#9CA3AF',
        },
        ticks: {
          color: '#9CA3AF',
          stepSize: 1,
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
    },
  };

  const hourlyClicksChartData = {
    labels: Object.keys(analyticsData.hourlyClicksData).map(hour => `${hour}:00`),
    datasets: [
      {
        label: 'Emails Clicked',
        data: Object.values(analyticsData.hourlyClicksData),
        backgroundColor: '#60A5FA', // Blue color
        borderColor: '#3B82F6',
        borderWidth: 1,
      },
    ],
  };

  const hourlyClicksChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Emails Clicked by Hour of Day',
        color: '#FFFFFF',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Hour of Day (24h)',
          color: '#9CA3AF',
        },
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of Clicks',
          color: '#9CA3AF',
        },
        ticks: {
          color: '#9CA3AF',
          stepSize: 1,
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
    },
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <h1 className="text-3xl font-bold text-text-white mb-6">Analytics for: {blogTitle || 'Blog'}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center min-h-[120px] flex flex-col justify-center">
          <div className="text-4xl font-bold text-accent-red mb-2">{analyticsData.summary.totalSent}</div>
          <div className="text-lg text-text-gray">Emails Sent</div>
        </div>
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center min-h-[120px] flex flex-col justify-center">
          <div className="text-4xl font-bold text-green-500 mb-2">{analyticsData.summary.totalOpened}</div>
          <div className="text-lg text-text-gray">Emails Opened</div>
        </div>
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center min-h-[120px] flex flex-col justify-center">
          <div className="text-4xl font-bold text-blue-500 mb-2">{analyticsData.summary.totalClicked}</div>
          <div className="text-lg text-text-gray">Emails Clicked</div>
        </div>
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center min-h-[120px] flex flex-col justify-center">
          <div className="text-4xl font-bold text-purple-500 mb-2">{analyticsData.summary.openRate}%</div>
          <div className="text-lg text-text-gray">Open Rate</div>
        </div>
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle text-center min-h-[120px] flex flex-col justify-center">
          <div className="text-4xl font-bold text-yellow-500 mb-2">{analyticsData.summary.clickRate}%</div>
          <div className="text-lg text-text-gray">Click Rate</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[350px]">
          <Pie data={openClickRateChartData} options={openClickRateChartOptions} />
        </div>
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[350px]">
          <Bar data={hourlyOpensChartData} options={hourlyOpensChartOptions} />
        </div>
        <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[350px]">
          <Bar data={hourlyClicksChartData} options={hourlyClicksChartOptions} />
        </div>
      </div>

      <div className="bg-gray-700 p-6 rounded-lg shadow-lg border border-border-subtle">
        <h2 className="text-xl font-bold text-text-white mb-4">Subscriber Activity</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-800 border border-gray-700">
            <thead>
              <tr>
                <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Email</th>
                <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Sent At</th>
                <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Opened At</th>
                <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Clicked At</th> {/* New column */}
                <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Status</th>
              </tr>
            </thead>
            <tbody>
              {analyticsData.subscribers.length > 0 ? (
                analyticsData.subscribers.map((sub, index) => (
                  <tr key={index} className="hover:bg-gray-700">
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{sub.email}</td>
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                      {sub.sent_at ? new Date(sub.sent_at).toLocaleString() : 'N/A'}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                      {sub.opened_at ? new Date(sub.opened_at).toLocaleString() : 'N/A'}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                      {sub.clicked_at ? new Date(sub.clicked_at).toLocaleString() : 'N/A'}
                    </td> {/* Display clicked_at */}
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        sub.status === 'Clicked' ? 'bg-blue-500' :
                        sub.status === 'Opened' ? 'bg-green-500' : 'bg-red-500'
                      }`}>
                        {sub.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-3 px-4 text-center text-sm text-text-gray">No subscriber activity found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BlogAnalytics;